import type { IPermissions } from '@interfaces/Permissions'
import type { PermissionsContract } from '@shell/contracts'
import { getCurrentPermission, getOverridePermission, getPermissionFromStorage } from '@utils/helpers/permissions'
import Rules from '@utils/rbacs'
import { getOrInitializeStore } from '@redux/redux'

// ---------------------------------------------------------------------------
// PermissionsAdapter — implements PermissionsContract
// ---------------------------------------------------------------------------

class PermissionsAdapter implements PermissionsContract {
  private _getState() {
    return getOrInitializeStore({}).getState()
  }

  /**
   * Check if the current user can access a given page by route path.
   * Looks up the permission whose `route` matches `path` and `type === 'page'`,
   * then evaluates it against the provided Cognito groups.
   */
  canAccessPage(path: string, cognitoGroups: string[], customerId?: string): boolean {
    const { permissions } = this._getState().permissions
    const permission = (Object.values(permissions) as IPermissions[]).find((p) => p.type === 'page' && p.route === path)

    if (!permission) return true

    return this._evaluate(permission, cognitoGroups, customerId)
  }

  /**
   * Check if a feature flag is enabled.
   * Reads from the `stored-ff` localStorage key (base64-encoded JSON).
   * Returns false in SSR context where localStorage is unavailable.
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (typeof window === 'undefined') return false
    return getPermissionFromStorage(flagKey)
  }

  /**
   * Get the denied action for a permission ID (e.g. 'redirect', 'maintenance').
   * Returns undefined if the user is allowed, or if no permission config exists.
   */
  getDeniedAction(permissionId: string): string | undefined {
    const state = this._getState()
    const permission = state.permissions.permissions[permissionId] as IPermissions | undefined
    if (!permission) return undefined

    const cognitoGroups = state.session?.cognitoGroups ?? []
    const allowed = this._evaluate(permission, cognitoGroups)
    return allowed ? undefined : permission.action
  }

  /**
   * Evaluate a permission against the current user's roles.
   * Returns the same permission object with `show` replaced by the computed
   * boolean result (true = user may see the content, false = denied).
   *
   * The returned object should be used for reading `.show` (evaluated access)
   * and other metadata fields (`.type`, `.action`, `.redirections`, …).
   * The original `show` field in the store is not mutated.
   */
  evaluatePermission(permission: IPermissions | undefined, cognitoGroups: string[], customerId?: string): IPermissions {
    if (!permission) {
      throw new Error(
        'PermissionsAdapter.evaluatePermission expected a permission object, but received undefined. Ensure the permission ID exists before evaluating it.',
      )
    }
    const allowed = this._evaluate(permission, cognitoGroups, customerId)
    return { ...permission, show: allowed }
  }

  /**
   * Evaluate the override (maintenance / coming-soon overlay) permission.
   * Returns true when `show=false` and the user is NOT explicitly blocked,
   * meaning a fallback overlay component should be rendered.
   */
  evaluateOverride(permission: IPermissions, cognitoGroups: string[], customerId?: string): boolean {
    const { session, userProfile } = this._getState()
    return (
      getOverridePermission({
        roles: cognitoGroups,
        userPermission: permission,
        userName: customerId ?? session?.user?.username ?? '',
        profile: userProfile?.info,
        data: null,
      }) ?? false
    )
  }

  /**
   * Evaluate a component-level permission by ID using the accessValidator logic.
   *
   * This method intentionally differs from `evaluatePermission` in one way:
   * it applies OR logic for dynamicFn — the static (group/user) check is run first
   * WITHOUT the dynamic function. If the static check passes, access is granted.
   * Only if the static check fails does the dynamic function run as a standalone
   * fallback. This preserves the existing behavior of RoleBasedView/accessValidator.
   *
   * @param configId - The permission ID (e.g. 'c::sidenav::calendar::student')
   * @param exclude - FeatureType exclusion map from RoleBasedView/exclude-config
   * @returns Whether the component should be rendered
   *
   * Not on PermissionsContract — only for use by RoleBasedView/accessValidator.
   */
  evaluateComponentById(configId: string, exclude: Record<string, string[]>): boolean {
    const { session, userProfile, permissions: permissionsState } = this._getState()
    const roles = session?.cognitoGroups ?? []
    const userName = session?.user?.username ?? ''
    const profile = userProfile?.info

    if (!roles.length) return false

    const permissionsMap = permissionsState?.permissions
    if (!userName || !permissionsMap || !permissionsMap[configId]) return false

    const permission = permissionsMap[configId] as IPermissions

    // Strip dynamicFn before the static check so that getCurrentPermission only
    // evaluates group/user rules. The dynamic function is checked separately below.
    const dynamicFn = permission.dynamicFn ?? ''

    // FeatureType exclusion: certain feature types hide specific component IDs.
    // This is part of the static check — if excluded, staticAllowed is false,
    // but dynamicFn can still grant access as a fallback override (preserving OR logic).
    const isExcluded = profile?.FeatureType?.some((feature: string) =>
      (exclude[feature] ?? []).includes(configId)
    )

    const staticAllowed =
      !isExcluded &&
      (getCurrentPermission({
        roles,
        userPermission: dynamicFn ? { ...permission, dynamicFn: undefined } : permission,
        userName,
        profile,
        data: null,
      }) ?? false)

    if (staticAllowed) return true

    // Dynamic fallback: if the dynamic RBAC function passes alone, grant access.
    if (dynamicFn && typeof (Rules as Record<string, unknown>)[dynamicFn] === 'function') {
      // @ts-ignore
      return Boolean(Rules[dynamicFn]({ profile, data: null, roles }))
    }

    return false
  }

  /**
   * Validate that every `dynamicFn` string referenced in the permissions config
   * maps to an actual function in rbacs.ts. Logs a warning for any missing entry.
   *
   * Call this once after permissions are loaded into Redux (see layouts/Permissions).
   * This is not on the PermissionsContract — it is a startup diagnostic only.
   */
  validateDynamicFunctions(permissions: IPermissions[]): void {
    const invalid: string[] = []
    permissions.forEach((p) => {
      if (p.dynamicFn && typeof (Rules as Record<string, unknown>)[p.dynamicFn] !== 'function') {
        invalid.push(`${p.id}: dynamicFn "${p.dynamicFn}" not found in rbacs.ts`)
      }
    })
    if (invalid.length > 0) {
      console.warn('[PermissionsAdapter] Unknown dynamicFn references:', invalid)
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Core evaluation: calls getCurrentPermission with data read from the Redux store.
   * `customerId` maps to `userName` for user-level allow/disallow checks.
   */
  private _evaluate(permission: IPermissions, cognitoGroups: string[], customerId?: string): boolean {
    const { session, userProfile } = this._getState()
    return (
      getCurrentPermission({
        roles: cognitoGroups,
        userPermission: permission,
        userName: customerId ?? session?.user?.username ?? '',
        profile: userProfile?.info,
        data: null,
      }) ?? false
    )
  }
}

// ---------------------------------------------------------------------------
// Singleton export — follows the same pattern as auth, routing, storage adapters.
// The concrete class is exported (not just PermissionsContract) so that callers
// can access validateDynamicFunctions(), which is not on the contract.
// All other consumers should type their reference as PermissionsContract.
// ---------------------------------------------------------------------------

let instance: PermissionsAdapter | null = null

export const getPermissionsAdapter = (): PermissionsAdapter => {
  if (!instance) instance = new PermissionsAdapter()
  return instance
}
